// Thin wrapper around Capacitor native plugins with web fallbacks.
// On Android (and iOS) we route geolocation + camera through the OS via
// Capacitor plugins so users get native permission prompts. In a browser
// (the existing CRM website) we fall through to the old Web APIs.

import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { Camera, CameraResultType, CameraSource, CameraDirection } from "@capacitor/camera";

export const isNative = Capacitor.isNativePlatform();

export async function getCurrentPosition({ timeout = 12000 } = {}) {
  if (isNative) {
    // Permission gate first so we can surface a clean error if the user
    // denied at the OS prompt. Geolocation.requestPermissions returns
    // {location: 'granted'|'denied'|'prompt'}.
    try {
      const perm = await Geolocation.checkPermissions();
      if (perm.location !== "granted") {
        const req = await Geolocation.requestPermissions();
        if (req.location !== "granted") {
          throw new Error("Location permission denied");
        }
      }
    } catch (e) {
      // Older devices return undefined for checkPermissions — just try the call.
    }
    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout,
      maximumAge: 0,
    });
    return {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
    };
  }

  // Web fallback — match browser callback API into the same shape.
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({
        latitude: p.coords.latitude,
        longitude: p.coords.longitude,
        accuracy: p.coords.accuracy,
      }),
      (err) => {
        const msg = err.code === err.PERMISSION_DENIED
          ? "Location permission denied"
          : err.code === err.POSITION_UNAVAILABLE
          ? "Location information unavailable"
          : err.code === err.TIMEOUT
          ? "Location request timed out"
          : "Unable to get location";
        reject(new Error(msg));
      },
      { enableHighAccuracy: true, timeout, maximumAge: 0 }
    );
  });
}

// captureSelfie: returns { dataUrl: string|null }. The backend only needs the
// boolean fact-of-capture (selfie_verified), but we hand back a preview URL
// so the UI can show a thumbnail.
export async function captureSelfie() {
  if (!isNative) {
    throw new Error("Native camera is only available in the mobile app");
  }
  try {
    const perm = await Camera.checkPermissions();
    if (perm.camera !== "granted") {
      const req = await Camera.requestPermissions({ permissions: ["camera"] });
      if (req.camera !== "granted") {
        throw new Error("Camera permission denied");
      }
    }
  } catch (e) {
    // Permission API may not exist on older versions — fall through.
  }
  const photo = await Camera.getPhoto({
    resultType: CameraResultType.DataUrl,
    source: CameraSource.Camera,
    direction: CameraDirection.Front,
    quality: 70,
    correctOrientation: true,
    allowEditing: false,
    saveToGallery: false,
  });
  return { dataUrl: photo.dataUrl || null };
}
